import React from 'react'
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  Badge,
} from '@mui/material'
import {
  Menu as MenuIcon,
  AccountCircle,
  Settings,
  ExitToApp,
  LightMode,
  DarkMode,
  KeyboardArrowDown,
} from '@mui/icons-material'
import { useTheme, alpha } from '@mui/material/styles'
import { useColorMode } from '../../../context/ThemeContext.jsx'
import { useDispatch, useSelector } from 'react-redux'
import { logout } from '../../../store/slices/authSlice.js'
import { useNavigate } from 'react-router-dom'
import { Tooltip } from '@mui/material'

const Header = ({ sidebarOpen, onSidebarToggle, isMobile }) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const theme = useTheme()
  const colorMode = useColorMode()
  const { user, userType } = useSelector((state) => state.auth)
  const [anchorEl, setAnchorEl] = React.useState(null)


  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  const handlePasswordAction = () => {
    handleMenuClose()
    if (userType === 'employee') {
      navigate('/change-password')
    } else {
      navigate('/reset-password')
    }
  }


  const handleLogout = () => {
    // Capture userType before logout clears it
    const currentUserType = userType

    dispatch(logout())
    // Redirect based on user type
    if (currentUserType === 'superadmin') {
      navigate('/super-admin/login')
    } else if (currentUserType === 'admin' || currentUserType === 'hr') {
      navigate('/org/login')
    } else {
      navigate('/login')
    }
  }

  const handleProfile = () => {
    handleMenuClose()
    // Redirect based on user type
    if (userType === 'employee') {
      navigate('/employee/profile')
    } else if (userType === 'superadmin') {
      navigate('/super-admin/profile')
    } else {
      navigate('/org/profile')
    }
  }

  const handleSettings = () => {
    handleMenuClose()
    // Redirect based on user type
    if (userType === 'employee') {
      navigate('/employee/settings')
    } else if (userType === 'superadmin') {
      navigate('/super-admin/settings')
    } else {
      navigate('/org/settings')
    }
  }

  const getGreeting = () => {
    const hour = new Date().getHours()
    if (hour < 12) return 'Good Morning'
    if (hour < 18) return 'Good Afternoon'
    return 'Good Evening'
  }

  const getAppTitle = () => {
    switch (userType) {
      case 'superadmin':
        return 'Super Admin Portal'
      case 'admin':
      case 'hr':
        return 'Organization Admin'
      case 'employee':
        return 'Employee Portal'
      default:
        return 'Face Recognition Attendance'
    }
  }

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        backgroundColor: (theme) => alpha(theme.palette.background.paper, 0.8),
        backdropFilter: 'blur(20px)',
        borderBottom: '1px solid',
        borderColor: 'divider',
        color: (theme) => theme.palette.text.primary,
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
        zIndex: (theme) => theme.zIndex.drawer + 1,
      }}
    >
      <Toolbar sx={{ minHeight: 76, px: { xs: 2, md: 3 } }}>
        <IconButton
          color="inherit"
          aria-label="open drawer"
          onClick={onSidebarToggle}
          edge="start"
          sx={{ mr: 2 }}
        >
          <MenuIcon />
        </IconButton>

        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Typography
            variant="subtitle1"
            noWrap
            component="div"
            fontWeight="600"
            sx={{
              lineHeight: 1.2,
              color: 'text.primary'
            }}
          >
            {getAppTitle()}
          </Typography>
          {user && (
            <Typography variant="caption" color="textSecondary" sx={{ lineHeight: 1 }}>
              {getGreeting()}, <Box component="span" sx={{ fontWeight: 500, color: 'primary.main' }}>{user.name?.split(' ')[0] || 'User'}</Box>
            </Typography>
          )}
        </Box>

        <Box sx={{ flexGrow: 1 }} />

        {user && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Tooltip title={theme.palette.mode === 'dark' ? "Light Mode" : "Dark Mode"}>
              <IconButton
                onClick={colorMode.toggleColorMode}
                color="inherit"
                size="small"
                sx={{
                  transition: 'all 0.2s',
                  '&:hover': { transform: 'scale(1.1)' }
                }}
              >
                {theme.palette.mode === 'dark' ? <LightMode fontSize="small" /> : <DarkMode fontSize="small" />}
              </IconButton>
            </Tooltip>


            <Box
              onClick={handleMenuOpen}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                cursor: 'pointer',
                p: 0.5,
                pr: 1,
                borderRadius: 4,
                border: '1px solid',
                borderColor: 'transparent',
                transition: 'all 0.2s',
                '&:hover': {
                  backgroundColor: (theme) => alpha(theme.palette.text.primary, 0.04),
                  borderColor: 'divider',
                }
              }}
              aria-controls={anchorEl ? 'account-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={anchorEl ? 'true' : undefined}
            >
              <Avatar
                sx={{
                  width: 28, // Reduced size
                  height: 28,
                  bgcolor: 'primary.main',
                  fontSize: '0.85rem',
                  fontWeight: 600
                }}
              >
                {user.name ? user.name.charAt(0).toUpperCase() :
                  userType === 'employee' ? 'E' : 'U'}
              </Avatar>
              <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                <Typography variant="subtitle2" fontWeight="600" lineHeight={1.2} fontSize="0.85rem">
                  {user.name || 'User'}
                </Typography>
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', lineHeight: 1, fontSize: '0.7rem' }}>
                  {userType === 'org_admin' ? 'Admin' : userType.charAt(0).toUpperCase() + userType.slice(1)}
                </Typography>
              </Box>
              <KeyboardArrowDown fontSize="small" color="action" />
            </Box>

            <Menu
              anchorEl={anchorEl}
              id="account-menu"
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              onClick={handleMenuClose}
              PaperProps={{
                elevation: 0,
                sx: {
                  overflow: 'visible',
                  filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                  mt: 1.5,
                  minWidth: 200,
                  borderRadius: 2,
                  '& .MuiAvatar-root': {
                    width: 32,
                    height: 32,
                    ml: -0.5,
                    mr: 1,
                  },
                  '&::before': {
                    content: '""',
                    display: 'block',
                    position: 'absolute',
                    top: 0,
                    right: 14,
                    width: 10,
                    height: 10,
                    bgcolor: 'background.paper',
                    transform: 'translateY(-50%) rotate(45deg)',
                    zIndex: 0,
                  },
                },
              }}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
              <MenuItem onClick={handleProfile}>
                <AccountCircle sx={{ mr: 2 }} />
                Profile
              </MenuItem>
              <MenuItem onClick={handleSettings}>
                <Settings sx={{ mr: 2 }} />
                Settings
              </MenuItem>
              <MenuItem onClick={handlePasswordAction}>
                <Settings sx={{ mr: 2 }} />
                {userType === 'employee' ? 'Change Password' : 'Reset Password'}
              </MenuItem>
              <Divider />
              <MenuItem onClick={handleLogout}>
                <ExitToApp sx={{ mr: 2 }} />
                Logout
              </MenuItem>
            </Menu>
          </Box>
        )}
      </Toolbar>


    </AppBar>
  )
}

export default Header